import React, { useState } from 'react';

const RuleForm = ({ rule, devices, onSave, onCancel, existingRuleIds, lockedDeviceId }) => {
  const isEditing = !!rule;

  const generateIdFromName = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  };

  const [formData, setFormData] = useState({
    id: rule?.id || '',
    name: rule?.name || '',
    enabled: rule?.enabled !== false,
    device_id: lockedDeviceId || rule?.device_id || '',
    parameter: rule?.parameter || 'temp',
    condition: {
      type: rule?.condition?.type || 'snapshot',
      operator: rule?.condition?.operator || 'gt',
      threshold: rule?.condition?.threshold || 0,
      duration_minutes: rule?.condition?.duration_minutes || 0,
      period_hours: rule?.condition?.period_hours || 24
    },
    severity: rule?.severity || 'warning',
    cooldown_minutes: rule?.cooldown_minutes || 480
  });

  const [errors, setErrors] = useState({});

  const selectedDevice = devices.find(d =>
    (d.device_id === formData.device_id) || (d.id === formData.device_id)
  );
  const availableParameters = selectedDevice?.monitored_parameters || ['temp', 'hum', 'co2', 'battery'];

  const getParameterUnit = (param) => {
    const units = { 'temp': 'C', 'hum': '%', 'co2': 'ppm', 'battery': '%' };
    return units[param] || '';
  };

  const handleChange = (field, value) => {
    if (field.startsWith('condition.')) {
      const conditionField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        condition: { ...prev.condition, [conditionField]: value }
      }));
    } else if (field === 'name' && !isEditing) {
      setFormData(prev => ({ ...prev, name: value, id: generateIdFromName(value) }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Rule name is required';

    if (!formData.id.trim()) {
      newErrors.id = 'Rule ID is required (auto-generated from name)';
    } else if (!isEditing && existingRuleIds.includes(formData.id)) {
      newErrors.id = 'A rule with this ID already exists';
    } else if (!/^[a-z0-9_]+$/.test(formData.id)) {
      newErrors.id = 'Invalid ID format';
    }

    if (!lockedDeviceId && !formData.device_id) newErrors.device_id = 'Device is required';

    if (formData.condition.threshold === '' || isNaN(formData.condition.threshold)) {
      newErrors.threshold = 'Valid threshold is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const cleanCondition = {
        type: formData.condition.type,
        operator: formData.condition.operator,
        threshold: Number(formData.condition.threshold)
      };

      if (formData.condition.type === 'snapshot' && formData.condition.duration_minutes > 0) {
        cleanCondition.duration_minutes = Number(formData.condition.duration_minutes);
      }

      if (formData.condition.type === 'average') {
        cleanCondition.period_hours = Number(formData.condition.period_hours);
      }

      onSave({
        ...formData,
        condition: cleanCondition,
        cooldown_minutes: Number(formData.cooldown_minutes)
      });
    }
  };

  const lockedDeviceName = lockedDeviceId
    ? (devices.find(d => (d.device_id === lockedDeviceId) || (d.id === lockedDeviceId))?.name || lockedDeviceId)
    : null;

  return (
    <div className="rule-form-overlay">
      <div className="rule-form">
        <h3>{isEditing ? 'Edit Rule' : 'Create Alert Rule'}</h3>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="rule-name">Name:</label>
            <input
              id="rule-name"
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Garage Temperature Low"
            />
            {errors.name && <span className="form-error">{errors.name}</span>}
            {!isEditing && formData.id && (
              <span className="form-hint">ID: {formData.id}</span>
            )}
            {isEditing && (
              <span className="form-hint">ID: {formData.id}</span>
            )}
            {errors.id && <span className="form-error">{errors.id}</span>}
          </div>

          <div className="form-group">
            <label className="setting-label">
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => handleChange('enabled', e.target.checked)}
              />
              Enabled
            </label>
            <span className="form-hint">Disabled rules won't trigger alerts</span>
          </div>

          <div className="form-group">
            <label htmlFor="device-select">Device:</label>
            {lockedDeviceId ? (
              <span style={{ fontSize: 14, color: '#333' }}>{lockedDeviceName}</span>
            ) : (
              <select
                id="device-select"
                value={formData.device_id}
                onChange={(e) => handleChange('device_id', e.target.value)}
              >
                <option value="">Select device...</option>
                {devices.map(device => (
                  <option key={device.device_id || device.id} value={device.device_id || device.id}>
                    {device.name || device.device_id || device.id}
                  </option>
                ))}
              </select>
            )}
            {errors.device_id && <span className="form-error">{errors.device_id}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="parameter-select">Parameter:</label>
            <select
              id="parameter-select"
              value={formData.parameter}
              onChange={(e) => handleChange('parameter', e.target.value)}
            >
              {availableParameters.map(param => (
                <option key={param} value={param}>
                  {param.charAt(0).toUpperCase() + param.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Condition Type:</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  name="condition-type"
                  checked={formData.condition.type === 'snapshot'}
                  onChange={() => handleChange('condition.type', 'snapshot')}
                />
                Snapshot (current value)
              </label>
              <label>
                <input
                  type="radio"
                  name="condition-type"
                  checked={formData.condition.type === 'average'}
                  onChange={() => handleChange('condition.type', 'average')}
                />
                Average (over time)
              </label>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="operator-select">Operator:</label>
              <select
                id="operator-select"
                value={formData.condition.operator}
                onChange={(e) => handleChange('condition.operator', e.target.value)}
              >
                <option value="lt">Less than (&lt;)</option>
                <option value="lte">Less or equal (&lt;=)</option>
                <option value="gt">Greater than (&gt;)</option>
                <option value="gte">Greater or equal (&gt;=)</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="threshold">Threshold:</label>
              <input
                id="threshold"
                type="number"
                step="any"
                value={formData.condition.threshold}
                onChange={(e) => handleChange('condition.threshold', e.target.value)}
              />
              {errors.threshold && <span className="form-error">{errors.threshold}</span>}
            </div>
          </div>

          {formData.condition.type === 'snapshot' && (
            <div className="form-group">
              <label htmlFor="duration">Duration (minutes, 0 = immediate):</label>
              <input
                id="duration"
                type="number"
                min="0"
                value={formData.condition.duration_minutes}
                onChange={(e) => handleChange('condition.duration_minutes', e.target.value)}
              />
              <span className="form-hint">Alert triggers after condition persists for this duration</span>
            </div>
          )}

          {formData.condition.type === 'average' && (
            <div className="form-group">
              <label htmlFor="period">Period (hours):</label>
              <input
                id="period"
                type="number"
                min="1"
                value={formData.condition.period_hours}
                onChange={(e) => handleChange('condition.period_hours', e.target.value)}
              />
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="severity-select">Severity:</label>
              <select
                id="severity-select"
                value={formData.severity}
                onChange={(e) => handleChange('severity', e.target.value)}
              >
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="cooldown">Cooldown (hours):</label>
              <input
                id="cooldown"
                type="number"
                min="1"
                value={formData.cooldown_minutes / 60}
                onChange={(e) => handleChange('cooldown_minutes', Number(e.target.value) * 60)}
              />
              <span className="form-hint">Time before re-alerting if condition persists</span>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="save-button">
              {isEditing ? 'Save Changes' : 'Create Rule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RuleForm;
